import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useMemo, useRef } from 'react';
import { GRID_LIMIT } from 'flow/utils/meeting-constants';
import { selectPinnedFeedNames, selectSpeakingMembers, selectVideoMembers } from 'flow/selectors/video-selectors';
import { getMembersInitialPositions, getNumberOfScreenShares, separateMemberTypes, sortMemberTypes } from 'flow/utils/meeting-video-grid-utils';
import { createStickyOrderState, reorderSticky } from 'flow/components/meeting/main-area/meeting-tile-utils';
import { useAppSelector } from './useRedux';
export var useMemberManagement = function useMemberManagement(meetingMembers, ownName) {
  var speakingMembers = useAppSelector(selectSpeakingMembers);
  var storeVideoMembers = useAppSelector(selectVideoMembers);
  var pinnedFeedNames = useAppSelector(selectPinnedFeedNames);
  var positionsInGrid = useRef([]);
  // Persistent sticky-order state for the render cap (see reorderSticky).
  var stickyRenderOrder = useRef(createStickyOrderState());

  // Memoized on its real inputs so the sort/position work (and the new result
  // arrays) only recompute when membership, member state, speaking activity or
  // the own name actually change — not on every unrelated re-render of the
  // grid (window resize, chat toggle, hover, etc.).
  var membersToRender = useMemo(function () {
    var members = meetingMembers.valueSeq();
    var videoMembers = storeVideoMembers.valueSeq();
    if (!members) {
      return {
        screenSharesToRender: [],
        nonScreenSharesToRender: [],
        totalFeedMemberCount: 0
      };
    }
    var numberOfScreenShares = getNumberOfScreenShares(videoMembers);
    var numberOfSlotsInGridScreenShareExcluded = GRID_LIMIT - numberOfScreenShares;
    positionsInGrid.current = getMembersInitialPositions(members, positionsInGrid.current);
    var typesLists = separateMemberTypes(members, videoMembers, ownName, positionsInGrid.current);
    var screenShareMemberList = typesLists.screenShareMemberList,
      videoMemberList = typesLists.videoMemberList,
      callerMemberList = typesLists.callerMemberList;
    sortMemberTypes(videoMemberList, screenShareMemberList, callerMemberList);
    var screenSharesToRender = [];
    var j = 0;
    while (j < screenShareMemberList.length && j < GRID_LIMIT) {
      screenSharesToRender.push(screenShareMemberList[j]);
      j++;
    }

    // Stable feed order: camera-on (video) members first, then dial-in
    // callers (each tier already join-ordered by sortMemberTypes). Keep
    // active speakers within the render cap — an off-cap speaker is pulled in
    // and stays (sticky) until a newer speaker displaces the longest-idle
    // occupant — without reshuffling feeds already inside the cap. The
    // visible-window equivalent runs later in MeetingMainArea against the
    // true on-screen count.
    var nonScreenSharesToRender = reorderSticky(stickyRenderOrder.current, videoMemberList.concat(callerMemberList), numberOfSlotsInGridScreenShareExcluded, function (item) {
      var _item$member;
      return (_item$member = item.member) === null || _item$member === void 0 ? void 0 : _item$member.get('key');
    }, function (item) {
      var _item$member2;
      return speakingMembers.has((_item$member2 = item.member) === null || _item$member2 === void 0 ? void 0 : _item$member2.get('displayName'));
    }).slice(0, numberOfSlotsInGridScreenShareExcluded);

    // A pinned participant must always render — it forms the stage — even
    // when the GRID_LIMIT cap above dropped them (the cap is order-based and
    // pin-unaware). Add back any pinned member the cap excluded so a pin is
    // honoured regardless of participant count.
    if (!pinnedFeedNames.isEmpty()) {
      var renderedKeys = new Set(nonScreenSharesToRender.map(function (m) {
        var _m$member;
        return (_m$member = m.member) === null || _m$member === void 0 ? void 0 : _m$member.get('key');
      }));
      var droppedPinned = videoMemberList.concat(callerMemberList).filter(function (m) {
        var _m$member2, _m$member3;
        return pinnedFeedNames.has((_m$member2 = m.member) === null || _m$member2 === void 0 ? void 0 : _m$member2.get('displayName')) && !renderedKeys.has((_m$member3 = m.member) === null || _m$member3 === void 0 ? void 0 : _m$member3.get('key'));
      });
      if (droppedPinned.length > 0) {
        nonScreenSharesToRender = nonScreenSharesToRender.concat(droppedPinned).sort(function (a, b) {
          return a.position - b.position;
        });
      }
    }
    // Uncapped count of feed-eligible members (video + dial-in callers,
    // excluding screen shares and self). The render lists above are capped
    // at GRID_LIMIT; this is the true total so the "+N" overflow indicator
    // can account for everyone not shown — not just those the engine hides.
    var totalFeedMemberCount = videoMemberList.length + callerMemberList.length;
    return {
      screenSharesToRender: screenSharesToRender,
      nonScreenSharesToRender: nonScreenSharesToRender,
      totalFeedMemberCount: totalFeedMemberCount
    };
  }, [meetingMembers, storeVideoMembers, speakingMembers, ownName, pinnedFeedNames]);
  return _objectSpread(_objectSpread({}, membersToRender), {}, {
    storeVideoMembers: storeVideoMembers
  });
};